import { ProductError } from "../../contracts/errors.ts";
import { migrateStorageFile } from "../../contracts/storage/migrations/runner.ts";
import { vocabularySnapshotSchema } from "../../contracts/storage/schemas.ts";
import type { MigrationContext, MigrationReport } from "../../contracts/storage/migrations/types.ts";
import type { VocabularySnapshot } from "../../domain/vocabulary/types.ts";
import type { FileDownload } from "../ports/platform.ts";
import type { ReplaceAllOptions, VocabularyStore } from "../ports/storage.ts";

export type PreparedBackup = {
  snapshot: VocabularySnapshot;
  report: MigrationReport;
};

export function createBackup(snapshot: VocabularySnapshot, exportedAt: string) {
  return {
    format: "vocably-backup" as const,
    formatVersion: 1 as const,
    exportedAt,
    snapshot,
  };
}

export async function exportVocabulary(
  store: VocabularyStore,
  download: FileDownload,
  exportedAt: string,
): Promise<void> {
  const snapshot = store.getSnapshot();
  if (!snapshot) {
    throw new ProductError("storage_unavailable", "Nothing to export yet.");
  }
  const backup = createBackup(snapshot, exportedAt);
  download.save("vocably-backup.json", JSON.stringify(backup, null, 2), "application/json");
}

export function prepareBackup(text: string, context: MigrationContext): PreparedBackup {
  const migrated = migrateStorageFile(text, context);
  if (migrated.status !== "ok") {
    throw productErrorForMigration(migrated.status, "message" in migrated ? migrated.message : undefined);
  }
  return {
    snapshot: vocabularySnapshotSchema.parse(migrated.snapshot),
    report: migrated.report,
  };
}

export function parseBackup(text: string, migratedAt: string): VocabularySnapshot {
  return prepareBackup(text, { migratedAt }).snapshot;
}

export async function restoreVocabulary(
  store: VocabularyStore,
  snapshot: VocabularySnapshot,
  options: ReplaceAllOptions = {},
) {
  return store.replaceAll(snapshot, options);
}

function productErrorForMigration(
  status: string,
  message?: string,
): ProductError {
  if (status === "unsupported_future" || status === "unsupported_old") {
    return new ProductError(
      "unsupported_version",
      message ?? "This backup was saved by a newer or unsupported Vocably version.",
    );
  }
  if (status === "malformed_json") {
    return new ProductError("storage_corrupt", "That file is not valid JSON.");
  }
  if (status === "conversion_failed" || status === "invalid_intermediate" || status === "invalid_target") {
    return new ProductError("migration_failed", message ?? "That backup could not be upgraded.");
  }
  return new ProductError("storage_corrupt", message ?? "That backup is not a Vocably vocabulary file.");
}

export function backupRestoreSummary(report: MigrationReport): string {
  if (report.noop) {
    return "Replace all local vocabulary with this backup?";
  }
  return `This backup is version ${report.sourceVersion} and will be upgraded to version ${report.targetVersion}. Replace all local vocabulary?`;
}
